export interface HotelSubscriptionAccessInput {
  /** `?tenantId=` — platform entry from TenantEditPage. */
  tenantId: string | undefined;
  canAdministerPlatformSubscriptions: boolean;
  canAdminHotelBrands: boolean;
  isTenantAdmin: boolean;
  canManageBilling: boolean;
}

/**
 * Who may open the hotel plan dialog. In tenant mode (`?tenantId=`) the
 * platform admin needs `platform:subscriptions:administrate`; otherwise the
 * tenant admin rules apply.
 */
export function canManageHotelSubscriptions({
  tenantId,
  canAdministerPlatformSubscriptions,
  canAdminHotelBrands,
  isTenantAdmin,
  canManageBilling,
}: HotelSubscriptionAccessInput): boolean {
  if (tenantId) {
    return canAdministerPlatformSubscriptions;
  }

  return canAdminHotelBrands && isTenantAdmin && canManageBilling;
}

export interface AssignHotelPlansInput {
  /** `?tenantId=` — platform entry from TenantEditPage. */
  tenantId: string | undefined;
  canAdministerPlatformSubscriptions: boolean;
  isPlatformAdmin: boolean;
}

/**
 * Who sees Assign plan / Unassign. Only SUPER_ADMIN and GUESTERIA_ADMIN
 * (owner decision, DV-13) and only in tenant mode, where `x-tenant-id` names
 * the tenant. Tenant roles (TENANT_ADMIN, HOTEL_ADMIN, FRONT_DESK, STAFF,
 * GUEST) only view the subscription.
 */
export function canAssignHotelPlans({
  tenantId,
  canAdministerPlatformSubscriptions,
  isPlatformAdmin,
}: AssignHotelPlansInput): boolean {
  return (
    isPlatformAdmin && Boolean(tenantId) && canAdministerPlatformSubscriptions
  );
}

export interface PlanActionVisibilityInput {
  canAssign: boolean;
  canManage: boolean;
  canMutateExtras: boolean;
  isCurrent: boolean;
  paymentRequired: boolean;
}

/**
 * Buttons of a plan card: the primary one is Pay now (for whoever manages the
 * tenant billing) or Assign plan (assign right only); Unassign needs the
 * assign right and is shown on the current plan only.
 */
export function getPlanActionVisibility({
  canAssign,
  canManage,
  canMutateExtras,
  isCurrent,
  paymentRequired,
}: PlanActionVisibilityInput): { showPrimary: boolean; showRemove: boolean } {
  return {
    showPrimary: paymentRequired ? canManage : canAssign,
    showRemove: canAssign && canMutateExtras && isCurrent,
  };
}

/** Plans list is loaded for exactly those who may open the dialog. */
export function canLoadHotelSubscriptionPlans(
  input: HotelSubscriptionAccessInput,
): boolean {
  if (input.tenantId) {
    return input.canAdministerPlatformSubscriptions;
  }

  return input.isTenantAdmin;
}

/**
 * Detach, pay-now and feature toggles are not opened to the platform in
 * tenant mode (backend, round 2): only assigning a plan and viewing remain.
 */
export function canMutateSubscriptionExtras(
  tenantId: string | undefined,
): boolean {
  return !tenantId;
}

/** Request options carrying `x-tenant-id` in tenant mode, nothing otherwise. */
export function tenantHeadersOptions(
  tenantId: string | undefined,
): { headers: Record<string, string> } | undefined {
  return tenantId ? { headers: { "x-tenant-id": tenantId } } : undefined;
}
